import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import "../styles/CreateCustomerAccount.css";
import "react-phone-input-2/lib/style.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { agentService } from "../services/AgentServices";
import { useEffect } from "react";

function CreateAgentAccount() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressLine3, setAddressLine3] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCounty, setSelectedCounty] = useState(
    "England - City of London"
  );
  const [selectedCountry, setSelectedCountry] = useState("United Kingdom");

  // Admin Area States
  const [agentRole, setAgentRole] = useState(null);
  const [assignedAreas, setAssignedAreas] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [areaFilters, setAreaFilters] = useState({
    search: ''
  });
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Role options
  const roleOptions = [
    { value: "MiniBusinessAdsAgent", label: "Mini Business Ads Agent" },
    { value: "SeniorBusinessAdsAgent", label: "Senior Business Ads Agent" },
  ];

  useEffect(() => {
    setRegion(selectedCounty);
    setCountry(selectedCountry);
  }, []);
  
  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null); 
  const [emailCheckMessage, setEmailCheckMessage] = useState("");

  // Phone validation states
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);
  const [phoneCheckMessage, setPhoneCheckMessage] = useState("");

  // Validation states
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [cityError, setCityError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");
  const [roleError, setRoleError] = useState("");

  // Check if selected role is Senior
  const isSeniorAgent = agentRole?.value === 'SeniorBusinessAdsAgent';

  // Fetch available admin areas when step 4 becomes active
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      fetchAvailableAreas();
    }
  }, [step, isSeniorAgent]);

  // Debounce search filter for admin areas
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      const timer = setTimeout(() => {
        fetchAvailableAreas();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [areaFilters, step, isSeniorAgent]);

  // Fetch available admin areas
  const fetchAvailableAreas = async () => {
    setLoadingAreas(true);
    try {
      const params = {
        search: areaFilters.search,
        per_page: 100
      };
      
      if (areaFilters.search === '') delete params.search;

      const response = await agentService.getAvailableAdminAreas(params);
      if (response.success) {
        setAvailableAreas(response.data.admin_areas);
      }
    } catch (error) {
      console.error("Error fetching available areas:", error);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Toggle area selection
  const toggleAreaSelection = (area) => {
    if (selectedAreas.some(a => a.admin_area_id === area.admin_area_id)) {
      setSelectedAreas(selectedAreas.filter(a => a.admin_area_id !== area.admin_area_id));
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  // Select all visible areas
  const selectAllVisible = () => {
    setSelectedAreas([...availableAreas]);
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedAreas([]);
  };

  // Function to get phone digit count
  const getPhoneDigitCount = () => {
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
    let nationalNumber = digitsOnly;
    if (match) {
      nationalNumber = digitsOnly.slice(match[0].length);
    }
    return nationalNumber.length;
  };

  const hasValidPhoneDigits = getPhoneDigitCount() >= 9;

  // Check email and phone availability simultaneously
  const checkEmailAndPhoneAvailability = async () => {
    let emailValid = true;
    let phoneValid = true;

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      emailValid = false;
    }

    // Check phone format
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
    let nationalNumber = digitsOnly;
    if (match) {
      nationalNumber = digitsOnly.slice(match[0].length);
    }
    if (nationalNumber.length !== 9) {
      phoneValid = false;
    }

    if (!emailValid || !phoneValid) {
      return { emailAvailable: false, phoneAvailable: false };
    }

    // Set both to checking state
    setEmailChecking(true);
    setPhoneChecking(true);
    setEmailAvailable(null);
    setPhoneAvailable(null);
    setEmailCheckMessage("Checking email availability...");
    setPhoneCheckMessage("Checking phone availability...");

    try {
      // Make both API calls simultaneously
      const emailResponse = await agentService.checkEmailAvailability(email);
      const phoneResponse = { available: true, message: "Phone number is valid" };

      setEmailAvailable(emailResponse.available);
      setPhoneAvailable(phoneResponse.available);
      setEmailCheckMessage(emailResponse.message);
      setPhoneCheckMessage(phoneResponse.message);

      return {
        emailAvailable: emailResponse.available,
        phoneAvailable: phoneResponse.available
      };

    } catch (error) {
      console.error("Availability check error:", error);
      setEmailAvailable(null);
      setPhoneAvailable(null);
      setEmailCheckMessage("Error checking email availability");
      setPhoneCheckMessage("Error checking phone availability");
      
      return {
        emailAvailable: false,
        phoneAvailable: false
      };
    } finally {
      setEmailChecking(false);
      setPhoneChecking(false);
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;

      // Check both email and phone availability simultaneously
      const availability = await checkEmailAndPhoneAvailability();
      
      // Check email availability result
      if (availability.emailAvailable === false) {
        setEmailError("");
        return;
      }

      if (emailChecking) {
        setEmailError("Please wait while we check email availability");
        return;
      }

      if (availability.emailAvailable === null) {
        setEmailError("Unable to verify email availability. Please try again.");
        return;
      }

      // Check phone availability result
      if (availability.phoneAvailable === false) {
        setPhoneError("");
        return;
      }

      if (phoneChecking) {
        setPhoneError("Please wait while we check phone availability");
        return;
      }

      if (availability.phoneAvailable === null) {
        setPhoneError("Unable to verify phone availability. Please try again.");
        return;
      }

      // Only proceed if both are available
      if (availability.emailAvailable === true && availability.phoneAvailable === true) {
        setStep((prev) => Math.min(prev + 1, 4));
      }
    } else if (step === 2) {
      const isValid = validateAddressDetails();
      if (!isValid) return;
      setStep((prev) => Math.min(prev + 1, 4));
    } else if (step === 3) {
      // Validate role selection
      if (!agentRole) {
        setRoleError("Please select an agent role");
        return;
      }
      setRoleError("");
      
      if (isSeniorAgent) {
        setStep(4);
      } else {
        // For Mini agents, skip admin areas and proceed to security
        setStep(5);
      }
    } else if (step === 4 && isSeniorAgent) {
      // Validate admin area selection for Senior agents
      if (selectedAreas.length === 0) {
        setError("Please select at least one admin area for Senior Agent");
        return;
      }
      setError("");
      setStep(5);
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Build steps dynamically based on role
  const getSteps = () => {
    const baseSteps = [
      { label: "Basic details", sub: "Enter your basic details" },
      { label: "Address", sub: "Enter your address details" },
      { label: "Role", sub: "Select agent role" },
    ];

    if (isSeniorAgent) {
      baseSteps.push({ label: "Admin Areas", sub: "Assign admin areas" });
    }

    baseSteps.push({ label: "Security", sub: "Enter your password" });

    return baseSteps;
  };

  const steps = getSteps();

  const regionOptions = {
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",

      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",

      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",

      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
  };

  const validatePassword = (pwd) => {
    const minLength = /.{8,}/;
    const upper = /[A-Z]/;
    const lower = /[a-z]/;
    const number = /[0-9]/;
    const special = /[!@#$%^&*(),.?":{}|<>]/;

    if (!pwd) return "Password is required.";
    if (!minLength.test(pwd))
      return "Password must be at least 8 characters long.";
    if (!upper.test(pwd)) return "Password must include an uppercase letter.";
    if (!lower.test(pwd)) return "Password must include a lowercase letter.";
    if (!number.test(pwd)) return "Password must include a number.";
    if (!special.test(pwd)) return "Password must include a special character.";
    return "";
  };

  const validateBasicDetails = () => {
    let valid = true;

    // First name validation
    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      setFirstNameError("First name must contain only letters.");
      valid = false;
    } else if (firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      valid = false;
    } else {
      setFirstNameError("");
    }

    // Last name validation
    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      setLastNameError("Last name must contain only letters.");
      valid = false;
    } else if (lastName.trim().length < 3) {
      setLastNameError("Last name must be at least 3 letters long.");
      valid = false;
    } else {
      setLastNameError("");
    }

    // Email validation
    if (!email.trim()) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      // Clear basic format error, but keep availability error if any
      if (!emailError.includes("already registered") && !emailError.includes("wait") && !emailError.includes("complete")) {
        setEmailError("");
      }
    }

    // Phone validation
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      valid = false;
    } else {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
      let nationalNumber = digitsOnly;
      if (match) {
        nationalNumber = digitsOnly.slice(match[0].length);
      }
      
      if (nationalNumber.length !== 9) {
        setPhoneError("Phone number must be exactly 9 digits (excluding country code).");
        valid = false;
      } else {
        // Clear basic format error, but keep availability error if any
        if (!phoneError.includes("already registered") && !phoneError.includes("wait") && !phoneError.includes("complete")) {
          setPhoneError("");
        }
      }
    }

    return valid;
  };

  const validateAddressDetails = () => {
    let valid = true;

    // Address line 1 validation
    if (!addressLine1.trim()) {
      valid = false;
    }

    // City validation
    if (!city.trim()) {
      setCityError("City is required.");
      valid = false;
    } else {
      setCityError("");
    }

    // Postal code validation
    if (!postalCode.trim()) {
      setPostalCodeError("Postal code is required.");
      valid = false;
    } else if (!/^[A-Za-z0-9\s\-]+$/.test(postalCode)) {
      setPostalCodeError("Please enter a valid postal code.");
      valid = false;
    } else {
      setPostalCodeError("");
    }

    return valid;
  };

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
    const randomPass = Array.from({ length: 10 }, () =>
      chars.charAt(Math.floor(Math.random() * chars.length))
    ).join("");

    setGeneratedPassword(randomPass);
    setConfirmCopy(false);
    setShowModal(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  // Step Validation
  const validateStep = () => {
    if (step === 1) {
      return firstName && lastName && email && phoneNumber && hasValidPhoneDigits;
    } else if (step === 2) {
      return addressLine1 && city && region && postalCode && country;
    } else if (step === 3) {
      return agentRole !== null;
    } else if (step === 4 && isSeniorAgent) {
      return selectedAreas.length > 0;
    } else if (step === 5 || (!isSeniorAgent && step === 4)) {
      return password && confirmPassword && password === confirmPassword;
    }
    return false;
  };

  const handleCreateAccount = async () => {
    if (!validateStep()) {
      alert("Please fill all required fields before creating the account.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const agentData = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone_number: phoneNumber,
        password: password,
        password_confirmation: confirmPassword,
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_line3: addressLine3,
        city: city,
        postcode: postalCode,
        country_name: country,
        region: region,
        role: agentRole.value,
        status: "approved",
      };

      console.log("Creating agent with data:", agentData);

      const response = await agentService.createAgent(agentData);

      // If Senior Agent and areas selected, assign them after creation
      if (isSeniorAgent && selectedAreas.length > 0 && response.data?.agent_id) {
        try {
          await agentService.assignAdminAreas(
            response.data.agent_id,
            selectedAreas.map(area => area.admin_area_id),
            true // replace existing
          );
          console.log("Admin areas assigned successfully");
        } catch (areaError) {
          console.error("Error assigning admin areas:", areaError);
          // Don't fail the whole creation if area assignment fails
        }
      }

      console.log("Agent created successfully:", response);
      setOpenSnackbar(true);

      setTimeout(() => {
        navigate("/manage-agent");
      }, 3000);
    } catch (err) {
      console.error("Error creating agent:", err);

      if (err.response?.data?.errors?.email) {
        setError("Email address is already taken. Please use a different email.");
      }  else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to create agent account. Please try again.");
      }

      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  // Get current country option for select
  const currentCountryOption = country
    ? { value: country, label: country }
    : null;
  // Get current region option for select
  const currentRegionOption = region ? { value: region, label: region } : null;

  // Get current step index for progress bar
  const currentStepIndex = steps.findIndex((_, index) => index + 1 === step) + 1;

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        {/* Error Message */}
        {error && (
          <div className="error-message" style={{
            margin: "1rem 0",
            padding: "0.75rem",
            background: "#f8d7da",
            color: "#721c24",
            borderRadius: "4px",
            border: "1px solid #f5c6cb",
          }}>
            {error}
          </div>
        )}

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity="success"
            icon={
              <span style={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "rgba(51, 178, 51, 1",
              }}>
                ✔
              </span>
            }
            sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "25px",
              width: "100%",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            Account created successfully!
          </MuiAlert>
        </Snackbar>

        {/* Header */}
        <div className="create-header">
          <h2>Create Agent Account</h2>
          <p>After creating, agent will receive login credentials via email</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/manage-agent" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-agent" className="breadcrumb-link">Manage Agent</Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Create Agent Account</span>
        </div>

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <div className={`circle ${isCompleted ? "completed" : isActive ? "active" : ""}`}>
                    {isCompleted ? "✓" : stepNum}
                  </div>
                  <div className="step-text">
                    <p className={`step-label ${isActive ? "active-label" : ""}`}>{item.label}</p>
                    <p className={`step-subtext ${isActive ? "active-sub" : ''}`}>{item.sub}</p>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`line ${isCompleted ? "line-filled" : "line-empty"}`}></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>First name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError("");
                      }}
                      placeholder="Enter first name"
                    />
                    <div className="error-container">
                      {firstNameError && <p className="error-text">{firstNameError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Last name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError("");
                      }}
                      placeholder="Enter last name"
                    />
                    <div className="error-container">
                      {lastNameError && <span className="error-text">{lastNameError}</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email <span className="required">*</span></label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);
                        
                        // Reset availability status when email changes
                        setEmailAvailable(null);
                        setEmailCheckMessage("");
                        
                        if (emailError && !emailError.includes("already registered")) {
                          setEmailError("");
                        }
                      }}
                      placeholder="Enter email"
                      className={emailAvailable === false ? "email-taken" : emailAvailable === true ? "email-available" : ""}
                    />
                    
                    {/* Email availability status - only show after checking */}
                    {emailChecking && (
                      <div className="email-status checking">
                        Checking email availability...
                      </div>
                    )}
                    
                    {!emailChecking && emailCheckMessage && (
                      <div className={`email-status ${emailAvailable === true ? 'available' : emailAvailable === false ? 'taken' : ''}`}>
                        {emailCheckMessage}
                      </div>
                    )}
                    
                    <div className="error-container">
                      {emailError && <p className="error-text">{emailError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Phone Number <span className="required">*</span></label>
                    <div className={`phone-input-container ${phoneAvailable === false ? 'phone-taken' : phoneAvailable === true ? 'phone-available' : ''}`}>
                      <PhoneInput
                        country={"gb"}
                        value={phoneNumber}
                        onChange={(value, country) => {
                          const digitsOnly = value.replace(/\D/g, "");
                          const dialCode = country?.dialCode || "";
                          const dialCodeLength = dialCode.length;
                          let nationalNumber = digitsOnly.slice(dialCodeLength);

                          if (nationalNumber.length > 9) {
                            nationalNumber = nationalNumber.slice(0, 9);
                          }

                          const finalValue = `+${dialCode}${nationalNumber}`;
                          setPhoneNumber(finalValue);
                          
                          // Reset availability status when phone changes
                          setPhoneAvailable(null);
                          setPhoneCheckMessage("");
                          
                          if (phoneError && !phoneError.includes("already registered")) {
                            setPhoneError("");
                          }
                        }}
                        onKeyDown={(e) => {
                          const digitsOnly = phoneNumber.replace(/\D/g, "");
                          const dialCode = phoneNumber.match(/^\+(\d{1,3})/)?.[1] || "";
                          const nationalNumber = digitsOnly.slice(dialCode.length);

                          if (nationalNumber.length >= 9 && /[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{
                          name: "phone_number",
                          required: true,
                        }}
                        inputClass="phone-input"
                        containerClass="phone-container"
                      />
                    </div>
                    
                    {/* Phone availability status - only show after checking */}
                    {phoneChecking && (
                      <div className="phone-status checking">
                        Checking phone availability...
                      </div>
                    )}
                    
                    {!phoneChecking && phoneCheckMessage && (
                      <div className={`phone-status ${phoneAvailable === true ? 'available' : phoneAvailable === false ? 'taken' : ''}`}>
                        {phoneCheckMessage}
                      </div>
                    )}
                    
                    <div className="error-container">
                      {phoneError && <p className="error-text">{phoneError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Address</h3>
                <hr />
                <div className="form-grid">
                  {/* Row 1: Address line 1 and Address line 2 side by side */}
                  <div className="form-group">
                    <label>
                      Address line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="Enter address line 1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Address line 2</label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Enter address line 2"
                    />
                  </div>

                  {/* Row 2: Address line 3 - full width */}
                  <div className="form-group full-width">
                    <label>Address line 3</label>
                    <input
                      type="text"
                      value={addressLine3}
                      onChange={(e) => setAddressLine3(e.target.value)}
                      placeholder="Enter address line 3"
                    />
                  </div>

                  {/* Row 3: City and Region side by side */}
                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (cityError) setCityError("");
                      }}
                      placeholder="Enter city"
                    />
                    <div className="error-container">
                      {cityError && <p className="error-text">{cityError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Region <span className="required">*</span>
                    </label>
                    <Select
                      options={
                        selectedCountry && regionOptions[selectedCountry]
                          ? regionOptions[selectedCountry].map((r) => ({
                              value: r,
                              label: r,
                            }))
                          : []
                      }
                      value={{ value: selectedCounty, label: selectedCounty }}
                      onChange={(option) => {
                        setSelectedCounty(option.value);
                        setRegion(option.value);
                      }}
                      defaultValue={{ 
                        value: "England - City of London", 
                        label: "England - City of London" 
                      }}
                      placeholder="Select or type region"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  {/* Row 4: Post Code and Country side by side */}
                  <div className="form-group">
                    <label>
                      Post Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        if (postalCodeError) setPostalCodeError("");
                      }}
                      placeholder="Enter postal code"
                    />
                    <div className="error-container">
                      {postalCodeError && <p className="error-text">{postalCodeError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <Select
                      options={Object.keys(regionOptions).map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      value={{ value: selectedCountry, label: selectedCountry }}
                      onChange={(option) => {
                        setSelectedCountry(option.value);
                        setCountry(option.value);
                        setRegion("");
                        const regions = regionOptions[option.value];
                        if (regions && regions.length > 0) {
                          setRegion(regions[0]);
                          setSelectedCounty(regions[0]);
                        }
                      }}
                      defaultValue={{ value: "United Kingdom", label: "United Kingdom" }}
                      placeholder="Select or type country"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Select Role</h3>
                <hr />
                <div className="form-grid center-step">
                  <div className="form-group">
                    <label>
                      Agent Role <span className="required">*</span>
                    </label>
                    <Select
                      options={roleOptions}
                      value={agentRole}
                      onChange={setAgentRole}
                      placeholder="Select agent role"
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container">
                      {roleError && <p className="error-text">{roleError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && isSeniorAgent && (
              <div>
                <h3>Admin Areas</h3>
                <hr/>
                
                {/* Search Filter */}
                <div style={{ marginBottom: '20px', background: '#f5f5f5', padding: '15px', borderRadius: '6px' }}>
                  <h4>Filter Available Areas</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Search</label>
                      <input
                        type="text"
                        value={areaFilters.search}
                        onChange={(e) => setAreaFilters({ ...areaFilters, search: e.target.value })}
                        placeholder="Search by area name..."
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Available Areas */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4>Available Areas ({availableAreas.length})</h4>
                    <div>
                      <button 
                        onClick={selectAllVisible}
                        style={{ marginRight: '10px', background: 'none', border: 'none', color: 'rgba(0, 0, 160, 1)', cursor: 'pointer' }}
                      >
                        Select All
                      </button>
                      <button 
                        onClick={clearSelections}
                        style={{ background: 'none', border: 'none', color: 'rgba(0, 0, 160, 1)', cursor: 'pointer' }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingAreas ? (
                    <p>Loading...</p>
                  ) : availableAreas.length === 0 ? (
                    <p>No admin areas found</p>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '10px' }}>
                      {availableAreas.map(area => (
                        <div 
                          key={area.admin_area_id} 
                          style={{
                            padding: '10px',
                            margin: '5px 0',
                            background: selectedAreas.some(a => a.admin_area_id === area.admin_area_id) ? '#e3f2fd' : 'white',
                            borderRadius: '4px',
                            border: `1px solid ${selectedAreas.some(a => a.admin_area_id === area.admin_area_id) ? 'rgba(0, 0, 160, 1)' : '#e0e0e0'}`,
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleAreaSelection(area)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>{area.admin_name}</strong>
                              {area.county && <span style={{ color: '#666', marginLeft: '8px' }}>({area.county.county_name})</span>}
                              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                Towns: {area.towns_count}
                              </div>
                            </div>
                            {selectedAreas.some(a => a.admin_area_id === area.admin_area_id) && (
                              <span style={{ fontSize: '12px', background: 'rgba(0, 0, 160, 1)', color: 'white', padding: '2px 8px', borderRadius: '12px' }}>
                                Selected
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Count */}
                {selectedAreas.length > 0 && (
                  <div style={{ marginBottom: '10px', color: 'rgba(0, 0, 160, 1)' }}>
                    {selectedAreas.length} area(s) selected
                  </div>
                )}
              </div>
            )}

            {(!isSeniorAgent && step === 4) || (isSeniorAgent && step === 5) ? (
              <div>
                <h3>Security</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group password-group">
                    <label>
                      Password <span className="required">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <div className="password-input-column">
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPassword(value);
                            setPasswordError(validatePassword(value));
                          }}
                          placeholder="Enter password"
                        />
                        <div className="error-container">
                          {passwordError && (
                            <p className="error-text">{passwordError}</p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="text-generate-btn"
                        onClick={generatePassword}
                      >
                        Generate password
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-group confirm-password-group">
                  <label>
                    Confirm Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfirmPassword(value);
                      setConfirmPasswordError(
                        value !== password ? "Passwords do not match." : ""
                      );
                    }}
                    placeholder="Confirm password"
                  />
                  <div className="error-container">
                    {confirmPasswordError && (
                      <p className="error-text confirm-error-text">
                        {confirmPasswordError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step > 1 && (
              <button className="btn prev" onClick={prevStep} disabled={loading}>
                Previous
              </button>
            )}
            {step < (isSeniorAgent ? 5 : 4) && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={
                  !validateStep() || 
                  loading || 
                  emailChecking ||
                  phoneChecking
                }
                style={{
                  opacity: (
                    validateStep() && 
                    !loading && 
                    !emailChecking &&
                    !phoneChecking
                  ) ? 1 : 0.6,
                  cursor: (
                    validateStep() && 
                    !loading && 
                    !emailChecking &&
                    !phoneChecking
                  ) ? "pointer" : "not-allowed",
                }}
              >
                {emailChecking || phoneChecking ? "Checking..." : "Next"}
              </button>
            )}
            {((!isSeniorAgent && step === 4) || (isSeniorAgent && step === 5)) && (
              <button
                className="btn next"
                onClick={handleCreateAccount}
                disabled={!validateStep() || loading}
                style={{
                  opacity: validateStep() && !loading ? 1 : 0.6,
                  cursor: validateStep() && !loading ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Creating..." : "Create Account"}
              </button>
            )}
          </div>
        </div>

        {showModal && (
          <div className="password-modal-overlay">
            <div className="password-modal">
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>

              <h3 className="modal-title">Password Generator</h3>

              <div className="password-box">
                <input
                  type="text"
                  value={generatedPassword}
                  readOnly
                  className="generated-password-input"
                />
                <button className="regen-btn" onClick={generatePassword}>
                  Re-generate
                </button>
              </div>

              <div className="checkbox-area">
                <input
                  type="checkbox"
                  id="confirmCopy"
                  checked={confirmCopy}
                  onChange={(e) => setConfirmCopy(e.target.checked)}
                />
                <label htmlFor="confirmCopy">
                  You must tick the box after copy the password
                </label>
              </div>

              <button
                className="use-password-btn"
                disabled={!confirmCopy}
                onClick={() => {
                  setPassword(generatedPassword);
                  setConfirmPassword(generatedPassword);
                  setPasswordError("");
                  setConfirmPasswordError("");
                  setShowModal(false);
                }}
              >
                Use password
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateAgentAccount;